import classNames from "classnames";
import React, { useEffect, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useForm } from "react-hook-form";
import { Constants } from "../../../../constants/constants";
import { UpsellPanelSteps, UserAccessLevelType } from "../../../../constants/upsellPanelEnumsAndConstants";
import { handleUserSignupAnalytics, isRequiredAccessLevelMatching } from "../../../../constants/upsellPanelHelpers";
import useRecaptcha from "../../../../hooks/useRecaptcha";
import { isIntelligentInvestorDomain } from "../../../../utils/utils";
import { useUpsellWidgetRedirect } from "../../hooks/useRedirectContext";
import { useUpsellWidgetContainer } from "../../hooks/useUpsellWidgetContainerContext";
import { useUpsellWidgetModel } from "../../hooks/useUpsellWidgetModelContext";
import { useUpsellWidgetUserFormContext } from "../../hooks/useUpsellWidgetUserFormContext";
import { useCheckExistingUserMutation } from "../../services/hooks/useCheckExistingUserQuery";
import { useCreateUserMutation } from "../../services/hooks/useCreateUserMutation";
import OAuthArticlesLoginPanel from "../OAuthArticlesLoginPanel";
import ValidationErrorsSummary from "../UpSellWidgetContainer/ValidationErrorsSummary";
import {
    EmailInput,
    FirstNameInput,
    LastNameInput,
    MobilePhoneInput,
    PasswordInput,
    TermsAndConditionsInput
} from "./FormControls";
import { HowFoundList } from "./FormControls/HowFoundList";
import { ReferralCodeInput } from "./FormControls/ReferralCodeInput";
import { UpsellLoader } from "./FormControls/UpsellLoader";

const SignupAndLoginTab = ({ model, goToStep, boldClass }) => {
    if (model.isPromo || model.hideLogin) return null;

    const signUpClass = classNames(
        "text-color-deep-blue-2 full-width d-block py-2 font-19",
        isIntelligentInvestorDomain() ? "border-bottom-4-deep-blue-2" : "border-bottom-4-gray-51",
        boldClass
    );
    const loginClass = `cursor-pointer text-color-gray-16 full-width d-block py-2 border-bottom-4-gray-21-with-hover font-19 ${boldClass}`;

    return (
        <div className="row mb-3 d-flex">
            <div className="col-6 pr-0">
                <div className={signUpClass}>
                    Sign up
                </div>
            </div>
            <div className="col-6 pl-0">
                <a className={loginClass} onClick={() =>
                    goToStep(
                        model.showPricingPanel ? UpsellPanelSteps.Step18PricingPanel : UpsellPanelSteps.Step3Login
                    )}>
                    {model.showPricingPanel ? "Purchase" : "Log in"}
                </a>
            </div>
        </div>
    );
}

export const Step1SignUpForm = () => {
    const { boldClass, goToStep, baseProps, updateBaseProps, handleErrorResponse, onSubmitError, handleProductActivation, setIsUpsellContainerLoading } = useUpsellWidgetContainer();
    const { updateUserForm } = useUpsellWidgetUserFormContext();
    const model = useUpsellWidgetModel();
    const { redirectToUrl } = useUpsellWidgetRedirect();
    const [formErrors, setFormErrors] = useState(null);
    const [showErrorSummary, setShowErrorSummary] = useState(false);
    const { captchaToken, executeCaptcha, recaptchaRef, handleRecaptcha } = useRecaptcha();

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitted },
        trigger,
    } = useForm();

    const { mutate: checkUser } = useCheckExistingUserMutation();
    const { mutate: createUser } = useCreateUserMutation();

    const handleSuccessfulSignUp = (response) => {
        window.authenticateAllDomains(response);
        handleUserSignupAnalytics(response);
        handleUserSignup(response);
    };
    const handleUserSignup = (response) => {
        recaptchaRef.current?.reset();
        setIsUpsellContainerLoading(false);
        if (isRequiredAccessLevelMatching(model?.requiredAccessLevel, UserAccessLevelType.Professional)) {
            goToStep(UpsellPanelSteps.Step5UpgradeUser);
        } else if (model.byPassPhoneVerification) {
            handleProductActivation(response, redirectToUrl, model);
        } else if (model.subscriptionStatus === Constants.CHARGIFY_SUBSCRIPTION_PAST_DUE_STATUS) {
            const nextStep = model.subscriptionPaymentUpdated === "true"
                ? UpsellPanelSteps.Step11PastDueStatusPaymentDetailsUpdated
                : UpsellPanelSteps.Step10PastDueStatusPaymentFailed;
            goToStep(nextStep);
        } else {
            goToStep(UpsellPanelSteps.Step4MobileUpdate);
        }
    };

    const onSubmit = async (formData) => {
        setFormErrors(null);
        setShowErrorSummary(true);
        setIsUpsellContainerLoading(true);
        let token = captchaToken || await executeCaptcha();
        createUser(
            {
                ...formData,
                captchaResponse: token,
                targetBuid: model.targetBuid ?? BusinessUnits.IS,
                entryPoint: model.entryPoint
            },
            {
                onSuccess: (response) => {
                    if (response.status === Constants.RequestStatus.SUCCESS) {
                        setShowErrorSummary(false);
                        updateBaseProps({
                            user: {
                                ...baseProps.user,
                                email: formData.email,
                                firstName: formData.firstName,
                                subscriberId: response.subscriberid,
                                subscribernonce: response.subscribernonce,
                            },
                            entryPointUrl: response.entryPointUrl,
                            completedStep1: true,
                            refreshOnClose: true
                        });
                        handleSuccessfulSignUp(response);
                    } else if (response.status === Constants.RequestStatus.USERNAME_EXISTS) {
                        updateBaseProps({
                            existingUsernameEntered: true,
                            user: {
                                ...baseProps.user,
                                email: formData.email,
                            }
                        });
                        updateUserForm({
                            email: formData.email,
                            firstName: formData.firstName,
                            lastName: formData.lastName
                        });
                        setIsUpsellContainerLoading(false);
                        goToStep(UpsellPanelSteps.Step3Login);
                    } else if (response.errors) {
                        handleErrorResponse(response.errors, setFormErrors);
                    }
                    setIsUpsellContainerLoading(false);
                },
                onError: (error) => onSubmitError(error)
            });
    };

    const handleBlur = (emailVal) => {
        setIsUpsellContainerLoading(true);
        emailVal && checkUser(emailVal, {
            onSuccess: (response) => {
                setIsUpsellContainerLoading(false);
                if (response.Success) {
                    updateBaseProps({
                        existingUsernameEntered: true
                    });
                    goToStep(UpsellPanelSteps.Step3Login);
                }
            }
        });
    };

    const btnText = model.isEurekaFund
        ? "Add me to waiting list"
        : model.isCompetitionPage
            ? model.mainButtonText
            : "Sign up for free";

    return (
        <div className="step1">
            {isSubmitted && showErrorSummary && errors && (<div className="upsell-container-content">
                <ValidationErrorsSummary validationErrors={{ ...errors, ...formErrors }} />
            </div>)}
            {model.formTitle && <h4 className="form-title">{model.formTitle}</h4>}
            <SignupAndLoginTab model={model} goToStep={goToStep} boldClass={boldClass} />

            <form className="join-form-inline form-is-custom form-disable-pristine-error" onSubmit={handleSubmit(onSubmit)} noValidate>
                <div className="row">
                    <div className={model.isPromo ? " col-md-6 upsell-form-left" : "col-md-12"}>
                        <FirstNameInput register={register} errors={errors} />
                    </div>
                    <div className={model.isPromo ? " col-md-6 upsell-form-right" : "col-md-12"}>
                        <LastNameInput register={register} errors={errors} />
                    </div>
                    <div className="col-md-12">
                        <EmailInput register={register} errors={errors} handleBlur={handleBlur} trigger={trigger} />
                    </div>

                    <div className="col-md-12">
                        {model.passwordRequired && (
                            <PasswordInput register={register} errors={errors} placeholderText="Create Password" displayPasswordStrength={true} />
                        )}
                    </div>
                    {model.mobileRequired && (
                        <div className="col-md-12">
                            <MobilePhoneInput register={register} errors={errors} />
                        </div>
                    )}
                    {model.displayReferralCodeField && (
                        <ReferralCodeInput register={register} />
                    )}
                    {model.displayHowFoundList && (
                        <div className="col-md-12">
                            <HowFoundList register={register} errors={errors} />
                        </div>
                    )}
                    <div className="col-md-12 text-left">
                        <TermsAndConditionsInput register={register} errors={errors} />
                    </div>
                    {
                        !model.isPromo && !model.isSubscription && (
                            <div className="col-md-12 text-left">
                                {model.hideEmailModal ?
                                    <i className="fa fa-info-circle email-modal-icon">
                                        <a className="email-modal-link" href="/email-communications" target="_blank">What emails do members receive?</a>
                                    </i>
                                    : <i className="fa fa-info-circle email-modal-icon">
                                        <a className="email-modal-link" href="#" data-bs-toggle="modal" data-bs-target="#emailModal">What emails do members receive?</a>
                                    </i>
                                }
                            </div>
                        )}
                    <div className="col-md-12">
                        <div className="row row-sm">
                            <div className="col col-xs-12 col-sm-12 col-sm">
                                <button type="submit" className={`mb-0 btn btn-primary btn-fw font-19-no-line-height ${isIntelligentInvestorDomain() ? " font-weight-semibold" : "font-circular-std-bold"}`} id="main-submit-button">
                                    {btnText}
                                </button>
                                <div className="font-14 text-color-gray-16 pt-2">Sign up to receive emails</div>
                            </div>
                        </div>
                    </div>
                </div>
                {model.showNoCC && !model.showPricingPanel ?
                    <div className="mt-4 font-16 mx-auto">No credit card required</div>
                    : model.showPricingPanel || model.isCompetitionPage ?
                        <div className="mt-4 mx-auto font-17-sm-down">Already a member? <a href="" className={`${model.isCompetitionPage ? model.textPrimaryColor + "  text-underline" : 'link-primary'}`} onClick={() => goToStep(UpsellPanelSteps.Step3Login)}>Log in</a>
                        </div>
                        : null
                }
                <ReCAPTCHA
                    size="invisible"
                    ref={recaptchaRef}
                    sitekey={window.reCaptchaKey}
                    onChange={handleRecaptcha}
                />
            </form >
            <OAuthArticlesLoginPanel />
        </div>
    );
};
